/*
    AboutPayment.jsx
    サンタナム　リシ

    このコンポーネントは、寄付や支援に関する情報を提供し、ユーザーが支援内容を選択し、
    寄付手続きを進めるためのモーダルウィンドウを含んでいます。
*/

import React, { useState } from "react";
import { useCms } from "../../cms/CmsProvider";
import { motion, AnimatePresence } from "framer-motion";
import { FaHeart, FaUsers, FaGraduationCap, FaTimes, FaArrowRight, FaChevronLeft, FaChevronRight, FaDollarSign, FaExclamationTriangle, FaBuilding, FaHandsHelping, FaSpinner, FaCalendarAlt, FaInfoCircle, FaCreditCard, FaUniversity, FaShieldAlt } from "react-icons/fa";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import StripeProvider from "../payment/StripeProvider";
import PaymentForm from "../payment/PaymentForm";
import BankTransferInstructions from "../payment/BankTransferInstructions";
import { getStripeLocale } from "../../config/locale";

// この通知を表示するコンポーネント
// なぜかというと、AnimatePresenceを使うために親コンポーネントが必要だから
const CustomNotification = ({ message, isVisible, onClose }) => {
    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className="custom-notification-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="custom-notification"
                        initial={{ opacity: 0, scale: 0.8, y: -50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: -50 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="notification-icon">
                            <FaExclamationTriangle />
                        </div>
                        <p>{message}</p>
                        <button className="notification-close" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default function PaymentNiTuite() {
    const { t, text, items, site } = useCms("donate");
    
    // Create donation options from translations
    const defaultDonationOptions = [
        {
            id: 1,
            icon: <FaDollarSign />,
            title: t('payment.options.custom.title'),
            description: t('payment.options.custom.description'),
            amount: null,
            impact: t('payment.options.custom.impact'),
            type: "custom",
            isCustom: true
        },
        {
            id: 2,
            icon: <FaGraduationCap />,
            title: t('payment.options.materials.title'),
            description: t('payment.options.materials.description'),
            amount: 1500,
            impact: t('payment.options.materials.impact'),
            type: "preset"
        },
        {
            id: 3,
            icon: <FaUsers />,
            title: t('payment.options.community.title'),
            description: t('payment.options.community.description'),
            amount: 3000,
            impact: t('payment.options.community.impact'),
            type: "preset"
        },
        {
            id: 4,
            icon: <FaGraduationCap />,
            title: t('payment.options.education.title'),
            description: t('payment.options.education.description'),
            amount: 5000,
            impact: t('payment.options.education.impact'),
            type: "preset"
        },
        {
            id: 5,
            icon: <FaBuilding />,
            title: t('payment.options.facility.title'),
            description: t('payment.options.facility.description'),
            amount: 10000,
            impact: t('payment.options.facility.impact'),
            type: "preset"
        },
        {
            id: 6,
            icon: <FaHandsHelping />,
            title: t('payment.options.comprehensive.title'),
            description: t('payment.options.comprehensive.description'),
            amount: 30000,
            impact: t('payment.options.comprehensive.impact'),
            type: "preset"
        }
    ];

    // microCMS の「寄付1〜8」に名前が入っていればそちらを使う
    // （金額が空欄のメニューは「自由な金額」として扱います）
    const cmsIcons = [<FaGraduationCap />, <FaUsers />, <FaGraduationCap />, <FaBuilding />, <FaHandsHelping />];
    const cmsOptions = items("donationOptions", null);
    const donationOptions = cmsOptions
        ? cmsOptions.map((o, i) => {
            const amount = Number(o.amount);
            const isCustom = !amount;
            return {
                id: i + 1,
                icon: isCustom
                    ? <FaDollarSign />
                    : (defaultDonationOptions[i] && !defaultDonationOptions[i].isCustom
                        ? defaultDonationOptions[i].icon
                        : cmsIcons[i % cmsIcons.length]),
                title: o.title,
                description: o.description,
                amount: isCustom ? null : amount,
                impact: o.impact,
                type: isCustom ? "custom" : "preset",
                ...(isCustom ? { isCustom: true } : {}),
            };
        })
        : defaultDonationOptions;
    
    // Add notification state
    const [notification, setNotification] = useState({ show: false, message: '' });
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0); // 自由な金額を中央に表示
    const [customAmount, setCustomAmount] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [userInfo, setUserInfo] = useState({
        name: '',
        email: '',
        message: '',
        motivation: ''
    });
    const [paymentType, setPaymentType] = useState('one-time'); // Add payment type state
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [bankTransferData, setBankTransferData] = useState(null);

    const steps = [
        t('payment.step1.title'),
        t('payment.step2.title'), 
        t('payment.step3.title'),
        t('payment.step4.title'),
        t('payment.step5.title')
    ];

    // Notification function
    const showNotification = (message, type = 'info') => {
        if (type === 'error') {
            toast.error(message);
        } else if (type === 'success') {
            toast.success(message);
        } else {
            toast.info(message);
        }
    };    // Reset everything when modal closes
    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentStep(0);
        setSelectedOption(null);
        setCustomAmount('');
        setClientSecret('');
        setPaymentLoading(false);
        setPaymentMethod(null);
        setBankTransferData(null);
        setUserInfo({
            name: '',
            email: '',
            message: '',
            motivation: ''
        });
    };

    // Create PaymentIntent when reaching payment step
    const createPaymentIntent = async () => {
        setPaymentLoading(true);

        try {
            const amount = selectedOption?.isCustom
                ? parseFloat(customAmount)
                : selectedOption?.amount;

            if (!amount || amount < 50) {
                showNotification(t('payment.errors.minimumAmount'), 'error');
                setPaymentLoading(false);
                return false;
            }

            // Bank transfer flow
            if (paymentMethod === 'bank_transfer') {
                const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/payment/create-bank-transfer-intent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        amount: amount,
                        currency: 'jpy',
                        donationCategory: selectedOption?.title,
                        donorInfo: userInfo
                    }),
                });

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(result.message || t('payment.errors.preparationFailed'));
                }

                setBankTransferData(result);
                setPaymentLoading(false);
                return true;
            }

            // Card flow (existing)
            const donationData = {
                amount: amount,
                currency: 'jpy',
                donationType: paymentType,
                donationCategory: selectedOption?.title,
                donorInfo: userInfo
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/payment/create-payment-intent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(donationData),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || t('payment.errors.preparationFailed'));
            }

            setClientSecret(result.client_secret);
            setPaymentLoading(false);
            return true;

        } catch (error) {
            console.error('Payment Intent creation failed:', error);
            showNotification(error.message || t('payment.errors.preparationError'), 'error');
            setPaymentLoading(false);
            return false;
        }
    };

    // Handle payment success
    const handlePaymentSuccess = (paymentResult) => {
        console.log('Payment succeeded:', paymentResult);
        handleCloseModal();
        showNotification(t('payment.success.message'), 'success');
    };

    // Handle payment error
    const handlePaymentError = (error) => {
        console.error('Payment failed:', error);
        showNotification(error.message || t('payment.errors.paymentFailed'), 'error');
    };

    // Modified next step handler
    const handleNextStep = async () => {
        if (currentStep === 3) { // Moving to payment step (after payment type selection)
            const paymentReady = await createPaymentIntent();
            if (!paymentReady) return;
        }
        
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    };

    // Update the handleOptionSelect function
    const handleOptionSelect = (option) => {
        if (option.isCustom) {
            const amount = parseInt(customAmount);
            if (!amount || amount < 100) {

                // 通知を表示
                setNotification({ show: true, message: t('payment.errors.minimumCustomAmount') });
                
                // 3秒後に通知を自動的に閉じる
                setTimeout(() => {
                    setNotification({ show: false, message: '' });
                }, 3000);
                
                return;
            }
            setSelectedOption({
                ...option,
                amount: amount,
                impact: t('payment.options.custom.customImpact', { amount: amount.toLocaleString() })
            });
        } else {
            setSelectedOption(option);
        }
        setCurrentStep(0);
        setShowModal(true);
    };

    const closeNotification = () => {
        setNotification({ show: false, message: '' });
    };

    const handleNext = async () => {
        if (currentStep === 3) { // Moving to payment step (after payment type selection)
            if (!paymentMethod) {
                showNotification(t('payment.step4.methodQuestion'), 'error');
                return;
            }
            await handleNextStep();
        } else if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const nextSlide = () => {
        setCurrentSlide((prev) => {
            const next = (prev + 1) % donationOptions.length;
            return next;
        });
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => {
            const next = (prev - 1 + donationOptions.length) % donationOptions.length;
            return next;
        });
    };

    // Replace the getSlideStyle function with this robust version
    const circOffset = (index, current, total) => {
        // Returns shortest signed distance from currentSlide
        let d = (index - current) % total;
        if (d > total / 2) d -= total;
        if (d <= -total / 2) d += total;
        return d; // e.g. -1, 0, +1
    };

    const getSlideAnimation = (index) => {
        const offset = circOffset(index, currentSlide, donationOptions.length);
        
        // Only show left (-1), center (0), right (+1)
        const isVisible = Math.abs(offset) <= 1;
        
        return {
            x: offset * 260, // Cards slightly overlap
            scale: offset === 0 ? 1.0 : 0.75,
            opacity: isVisible ? (offset === 0 ? 1 : 0.45) : 0,
            zIndex: offset === 0 ? 10 : 5,
            pointerEvents: offset === 0 ? 'auto' : 'none',
            rotateY: offset * 6 // Subtle 3D rotation
        };
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.8 }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                duration: 0.6
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" }
        }
    };

    return (
        <section className="payment-section" id="donation">
            {/* Modern geometric decorations */}
            <div className="geometric-decoration-1"></div>
            <div className="geometric-decoration-2"></div>
            
            {/* 通知コンポーネントを追加 */}
            <CustomNotification
                message={notification.message}
                isVisible={notification.show}
                onClose={closeNotification}
            />
            
            <motion.div
                className="payment-container"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.3 }}
            >
                <motion.div className="payment-header" variants={itemVariants}>
                    <h2>{text("headerTitle", "payment.header.title")}</h2>
                    <p>{text("headerSubtitle", "payment.header.subtitle")}</p>
                </motion.div>

                <motion.div className="carousel-container" variants={itemVariants}>
                    <button className="carousel-btn prev" onClick={prevSlide}>
                        <FaChevronLeft />
                    </button>
                    
                    <div className="carousel-wrapper">
                        <div className="carousel-track">
                            {donationOptions.map((option, index) => (
                                <motion.div
                                    key={option.id}
                                    className={`carousel-card ${index === currentSlide ? 'active' : ''} ${option.isCustom ? 'custom-card' : ''}`}
                                    animate={getSlideAnimation(index)}
                                    transition={{ 
                                        duration: 0.9, 
                                        ease: [0.19, 1, 0.22, 1] // easeOutExpo for very smooth motion
                                    }}
                                    whileHover={index === currentSlide ? {
                                        scale: 1.05,
                                        rotateY: 0,
                                        transition: { duration: 0.3, ease: "easeOut" }
                                    } : {}}
                                >
                                    <div className="donation-icon">
                                        {option.icon}
                                    </div>
                                    <h3>{option.title}</h3>
                                    <p>{option.description}</p>
                                    
                                    {option.isCustom ? (
                                        <div className="custom-amount-section">
                                            <input
                                                type="number"
                                                value={customAmount}
                                                onChange={(e) => setCustomAmount(e.target.value)}
                                                placeholder={t('payment.customAmountPlaceholder')}
                                                className="custom-amount-input"
                                                min="100"
                                            />
                                            <div className="amount-display">
                                                ¥{customAmount ? parseInt(customAmount).toLocaleString() : '---'}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="donation-amount">
                                            ¥{option.amount.toLocaleString()}
                                        </div>
                                    )}
                                    
                                    <div className="donation-impact">
                                        {option.impact}
                                    </div>
                                    
                                    <AnimatePresence>
                                        {index === currentSlide && (
                                            <motion.button 
                                                className="select-button"
                                                onClick={() => handleOptionSelect(option)}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 20 }}
                                                transition={{ delay: 0.2 }}
                                            >
                                                {t('payment.selectButton')} <FaArrowRight />
                                            </motion.button>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                    
                    <button className="carousel-btn next" onClick={nextSlide}>
                        <FaChevronRight />
                    </button>
                </motion.div>

                <motion.div className="donation-disclaimer" variants={itemVariants}>
                    <p>{text("disclaimer", "payment.disclaimer")}</p>
                </motion.div>

                <motion.div className="carousel-indicators" variants={itemVariants}>
                    {donationOptions.map((_, index) => (
                        <button
                            key={index}
                            className={`indicator ${index === currentSlide ? 'active' : ''}`}
                            onClick={() => setCurrentSlide(index)}
                        />
                    ))}
                </motion.div>

                {/* 賛助会員セクション */}
                <motion.div className="supporting-member-section" variants={itemVariants}>
                    <h3>{text("memberTitle", "payment.supportingMember.title")}</h3>
                    <p>{text("memberDescription", "payment.supportingMember.description")}</p>
                    <a
                        href={site.memberFormUrl || "https://forms.gle/z8xSHYJ5PApRzgrSA"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="supporting-member-button"
                    >
                        {text("memberButton", "payment.supportingMember.registerButton")} <FaArrowRight />
                    </a>
                </motion.div>
            </motion.div>

            <AnimatePresence>
                {showModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleCloseModal}
                    >
                        <motion.div
                            className="modal-content"
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button 
                                className="modal-close"
                                onClick={handleCloseModal}
                            >
                                <FaTimes />
                            </button>

                            <div className="modal-header">
                                <div className="step-indicator">
                                    {steps.map((step, index) => (
                                        <div 
                                            key={index}
                                            className={`step ${index <= currentStep ? 'active' : ''}`}
                                        >
                                            {index + 1}
                                        </div>
                                    ))}
                                </div>
                                <h3>{steps[currentStep]}</h3>
                            </div>

                            <div className="modal-body">
                                {currentStep === 0 && selectedOption && (
                                    <div className="step-content">
                                        <div className="selected-option">
                                            <div className="option-icon">
                                                {selectedOption.icon}
                                            </div>
                                            <h4>{selectedOption.title}</h4>
                                            <p>{selectedOption.description}</p>
                                            <div className="amount">¥{selectedOption.amount.toLocaleString()}</div>
                                        </div>
                                        <p style={{ textAlign: 'center', fontWeight: 'bold', marginTop: '20px' }}>
                                            {t('payment.step1.confirmQuestion')}
                                        </p>
                                    </div>
                                )}

                                {currentStep === 1 && (
                                    <div className="step-content">
                                        <div className="form-group">
                                            <label>{t('payment.step2.nameLabel')}</label>
                                            <input
                                                type="text"
                                                value={userInfo.name}
                                                onChange={(e) => setUserInfo({...userInfo, name: e.target.value})}
                                                placeholder={t('payment.step2.namePlaceholder')}
                                            />
                                            <small className="form-hint">{t('payment.step2.nameHint')}</small>
                                        </div>
                                        <div className="form-group">
                                            <label>{t('payment.step2.emailLabel')}</label>
                                            <input
                                                type="email"
                                                value={userInfo.email}
                                                onChange={(e) => setUserInfo({...userInfo, email: e.target.value})}
                                                placeholder={t('payment.step2.emailPlaceholder')}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('payment.step2.motivationLabel')}</label>
                                            <select
                                                value={userInfo.motivation}
                                                onChange={(e) => setUserInfo({...userInfo, motivation: e.target.value})}
                                            >
                                                <option value="">{t('payment.step2.motivationPlaceholder')}</option>
                                                <option value="parent">{t('payment.step2.motivationOptions.parent')}</option>
                                                <option value="educator">{t('payment.step2.motivationOptions.educator')}</option>
                                                <option value="community">{t('payment.step2.motivationOptions.community')}</option>
                                                <option value="other">{t('payment.step2.motivationOptions.other')}</option>
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {currentStep === 2 && (
                                    <div className="step-content">
                                        <div className="form-group">
                                            <label>{t('payment.step3.messageLabel')}</label>
                                            <textarea
                                                value={userInfo.message}
                                                onChange={(e) => setUserInfo({...userInfo, message: e.target.value})}
                                                placeholder={t('payment.step3.messagePlaceholder')}
                                                rows="4"
                                            />
                                        </div>
                                        <div className="message-preview">
                                            <p><FaHeart style={{ color: 'var(--accent-pink)', marginRight: '8px' }} />{t('payment.step3.messageInfo')}</p>
                                        </div>
                                    </div>
                                )}

                                {currentStep === 3 && (
                                    <div className="step-content">
                                        <div className="payment-type-selection">
                                            <h4>{t('payment.step4.question')}</h4>

                                            {/* Payment method pill selector */}
                                            <div className="payment-method-pills">
                                                <button
                                                    type="button"
                                                    className={`payment-method-pill ${paymentMethod === 'card' ? 'active' : ''}`}
                                                    onClick={() => setPaymentMethod('card')}
                                                >
                                                    <FaCreditCard />
                                                    <span>{t('payment.step4.card.title')}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`payment-method-pill ${paymentMethod === 'bank_transfer' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setPaymentMethod('bank_transfer');
                                                        setPaymentType('one-time');
                                                    }}
                                                >
                                                    <FaUniversity />
                                                    <span>{t('payment.step4.bankTransfer.title')}</span>
                                                </button>
                                            </div>

                                            {/* Donation frequency selector */}
                                            <div className={`payment-type-options ${!paymentMethod ? 'disabled-group' : ''}`}>
                                                <button
                                                    type="button"
                                                    className={`payment-type-btn ${paymentType === 'one-time' && paymentMethod ? 'active' : ''} ${!paymentMethod ? 'disabled' : ''}`}
                                                    onClick={() => {
                                                        if (paymentMethod) setPaymentType('one-time');
                                                    }}
                                                    disabled={!paymentMethod}
                                                >
                                                    <FaHeart />
                                                    <span>{t('payment.step4.oneTime.title')}</span>
                                                    <small>{t('payment.step4.oneTime.description')}</small>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`payment-type-btn ${paymentType === 'recurring' && paymentMethod ? 'active' : ''} ${!paymentMethod || paymentMethod === 'bank_transfer' ? 'disabled' : ''}`}
                                                    onClick={() => {
                                                        if (paymentMethod && paymentMethod !== 'bank_transfer') {
                                                            setPaymentType('recurring');
                                                        }
                                                    }}
                                                    disabled={!paymentMethod || paymentMethod === 'bank_transfer'}
                                                >
                                                    <FaCalendarAlt />
                                                    <span>{t('payment.step4.recurring.title')}</span>
                                                    <small>{t('payment.step4.recurring.description')}</small>
                                                </button>
                                            </div>
                                            {paymentMethod && (
                                                <div className="payment-type-info">
                                                    {paymentMethod === 'bank_transfer' ? (
                                                        <p><FaInfoCircle /> {t('payment.step4.recurring.disabledInfo')}</p>
                                                    ) : paymentType === 'one-time' ? (
                                                        <p><FaInfoCircle /> {t('payment.step4.oneTime.info')}</p>
                                                    ) : (
                                                        <p><FaInfoCircle /> {t('payment.step4.recurring.info')}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {currentStep === 4 && (
                                    <div className="step-content">
                                        <div className="stripe-disclaimer-card">
                                            <p><FaShieldAlt style={{ color: 'var(--brand-orange)', marginRight: '5px', flexShrink: 0, position: 'relative', top: '2px' }} />{t('donatePage.stripeDisclaimer.text1')}</p>
                                            <p>{t('donatePage.stripeDisclaimer.text2')}</p>
                                        </div>
                                        {paymentLoading ? (
                                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                                <FaSpinner className="spinning" style={{ fontSize: '2rem', color: 'var(--brand-orange)', marginBottom: '20px' }} />
                                                <p>{t('payment.step5.preparing')}</p>
                                            </div>
                                        ) : paymentMethod === 'bank_transfer' && bankTransferData ? (
                                            <BankTransferInstructions
                                                data={bankTransferData}
                                                amount={selectedOption?.isCustom
                                                    ? parseFloat(customAmount)
                                                    : selectedOption?.amount}
                                                onClose={handleCloseModal}
                                            />
                                        ) : clientSecret ? (
                                            <StripeProvider
                                                clientSecret={clientSecret}
                                                locale={getStripeLocale()}
                                            >
                                                <PaymentForm
                                                    donationData={{
                                                        amount: selectedOption?.isCustom
                                                            ? parseFloat(customAmount)
                                                            : selectedOption?.amount,
                                                        currency: 'jpy',
                                                        donationCategory: selectedOption?.title,
                                                        donorInfo: userInfo
                                                    }}
                                                    clientSecret={clientSecret}
                                                    onSuccess={handlePaymentSuccess}
                                                    onError={handlePaymentError}
                                                    onBack={() => setCurrentStep(3)}
                                                />
                                            </StripeProvider>
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                                <p>{t('payment.step5.error')}</p>
                                                <button
                                                    className="next-button"
                                                    onClick={createPaymentIntent}
                                                    style={{ marginTop: '20px' }}
                                                >
                                                    {t('payment.step5.retry')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                {currentStep > 0 && currentStep !== 4 && (
                                    <button className="back-button" onClick={handleBack}>
                                        {t('payment.backButton')}
                                    </button>
                                )}
                                {currentStep < steps.length - 1 && currentStep !== 4 && (
                                    <button className="next-button" onClick={handleNext}>
                                        {t('payment.nextButton')}
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </section>
    );
}